/**
 * Derivation of the full brand palette from the single colour an organization actually picks.
 *
 * The Appearance tab stores one hex value. Everything else the UI needs — hover and active
 * states, the pale surface tints, a readable foreground, a focus ring — is computed here rather
 * than stored, so that an organization cannot end up with a half-configured palette and so that
 * adding a new shade never requires a schema change.
 *
 * The derivation is calibrated against the existing hardcoded Convay palette: feeding it
 * #365d7e yields the hover and active shades the stylesheets already use by hand (#2b5273,
 * #294760) to within a couple of hex digits.
 *
 * Kept deliberately identical to cm-frontend's copy apart from the platform defaults, so the two
 * applications derive the same palette from the same organization colour. They are separate
 * repositories with no shared package, so this is duplicated rather than imported; if the
 * derivation changes in one, it must change in both.
 */

/** Platform fallback, used whenever an organization has no deliberate choice. */
export const PLATFORM_BRAND = '#365d7e';
export const PLATFORM_FONT = 'Lato';

/**
 * The values the Appearance tab's font picker can produce. Anything outside this list is
 * rejected rather than rendered: the font name is interpolated into a Google Fonts URL.
 */
export const FONT_ALLOWLIST: readonly string[] = [
  'Roboto Mono', 'Fira Code', 'JetBrains Mono',
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Source Sans Pro',
  'Poppins', 'Nunito', 'Raleway', 'Work Sans', 'DM Sans', 'Manrope',
  'Merriweather', 'Playfair Display', 'Lora',
];

/**
 * Picker labels whose Google Fonts family name differs from the label.
 *
 * Currently empty: every name in FONT_ALLOWLIST is served by Google under exactly that name.
 * ("Source Sans Pro" is frozen at v23 rather than retired — it still resolves, and requesting
 * "Source Sans 3" instead loads a family the stylesheet never asks for, so the text silently
 * falls through to the next entry in the stack.)
 *
 * If a family is ever genuinely renamed, adding it here is enough: both the stylesheet request
 * and the CSS font-family go through this same map, so the two cannot drift apart.
 */
const GOOGLE_FAMILY_OVERRIDES: Record<string, string> = {};

/**
 * The family name to use in CSS. Must always agree with what {@link googleFontHref} requests,
 * or the browser loads one family while the stylesheet asks for another and the text renders in
 * whatever comes next in the font stack.
 */
export function cssFontFamily(font: string): string {
  const allowed = normaliseFont(font);
  if (!allowed) return PLATFORM_FONT;
  return GOOGLE_FAMILY_OVERRIDES[allowed] || allowed;
}

/**
 * The legacy Appearance form initialised itself with this exact pair and the whole organization
 * body is re-sent on every save, so they were persisted for organizations whose admin never
 * opened the tab. The backend already normalises them away; this mirrors the rule client-side so
 * a stale cache or an older backend cannot repaint a tenant in monospace grey.
 *
 * Matched as a PAIR, never individually: "Roboto Mono" and #7A8699 are each legitimate choices
 * on their own, and treating them separately would silently swap a deliberately chosen
 * Roboto Mono for the platform font.
 */
const LEGACY_DEFAULT_FONT = 'Roboto Mono';
const LEGACY_DEFAULT_COLOR = '#7a8699';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Colours an organization may not use as its brand.
 *
 * White is excluded because the brand is used as a *foreground* in many places — link text,
 * active nav labels, icon strokes, chart lines — all of which sit on a white page. A white brand
 * makes them invisible rather than merely low-contrast, and unlike a pale colour that cannot be
 * rescued by flipping the foreground ink.
 *
 * Deliberately an exact match rather than a lightness threshold: pale brands such as #A6E0AB are
 * legitimate and already render correctly, because --brand-contrast switches to dark ink for
 * them. Only pure white is unusable.
 */
const DISALLOWED_BRAND_COLORS = new Set(['#ffffff']);

export const INVALID_HEX_MESSAGE =
  'Enter a six-digit hex colour, for example #0066FF. Leave blank to use the platform default.';

export const WHITE_NOT_ALLOWED_MESSAGE =
  'White cannot be used as a brand colour, because buttons, links and icons would be invisible ' +
  'against the page. Choose a darker colour, or leave blank to use the platform default.';

/**
 * Single source of truth for brand-colour validation.
 *
 * @returns the message to show the admin, or null when the value is acceptable.
 *          An empty value is acceptable and means "use the platform default".
 */
export function brandColorError(value: string | null | undefined): string | null {
  const hex = (value || '').trim();
  if (!hex) return null;
  if (!HEX_RE.test(hex)) return INVALID_HEX_MESSAGE;
  if (DISALLOWED_BRAND_COLORS.has(hex.toLowerCase())) return WHITE_NOT_ALLOWED_MESSAGE;
  return null;
}

export interface BrandTokens {
  brand: string;
  brandHover: string;
  brandActive: string;
  brandLight: string;
  brandContrast: string;
  tint50: string;
  tint100: string;
  tint200: string;
  ring: string;
}

interface Rgb { r: number; g: number; b: number }

export function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

/** True only when font and colour together are the exact pair the old form saved. */
function isLegacyUntouchedPair(font: unknown, color: unknown): boolean {
  return (
    typeof font === 'string' &&
    typeof color === 'string' &&
    font.trim() === LEGACY_DEFAULT_FONT &&
    color.trim().toLowerCase() === LEGACY_DEFAULT_COLOR
  );
}

/**
 * @param color the colour stored alongside it, needed for the pairwise legacy check.
 * @returns the font to use, or null when the stored value is absent or not a real choice.
 */
export function normaliseFont(value: unknown, color?: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (isLegacyUntouchedPair(value, color)) return null;
  const font = value.trim();
  if (!font) return null;
  return FONT_ALLOWLIST.includes(font) ? font : null;
}

/**
 * @param font the font stored alongside it, needed for the pairwise legacy check.
 * @returns the colour to use, or null when the stored value is absent or not a real choice.
 */
export function normaliseBrandColor(value: unknown, font?: unknown): string | null {
  if (!isValidHex(value)) return null;
  if (isLegacyUntouchedPair(font, value)) return null;
  const hex = value.trim().toLowerCase();
  // A disallowed colour that somehow reached storage must not render either.
  return DISALLOWED_BRAND_COLORS.has(hex) ? null : hex;
}

/**
 * Builds the Google Fonts stylesheet URL for a font.
 *
 * Requests explicit weights: the previous markup asked for families with no `:wght@` specifier,
 * which returns weight 400 only and leaves every heading and bold run to be synthesised by the
 * browser.
 */
export function googleFontHref(font: string): string | null {
  if (!normaliseFont(font)) return null;

  // Same source of truth as the CSS font-family, so the loaded family and the requested family
  // can never disagree.
  const family = cssFontFamily(font).replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700&display=swap`;
}

function hexToRgb(hex: string): Rgb {
  const v = hex.trim().replace('#', '');
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }

  const hue = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
}

/** Adjusts lightness proportionally, so pale brands shift as visibly as dark ones. */
function shiftLightness(hex: string, factor: number): string {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  const next = factor < 0
    ? l * (1 + factor)          // darken: proportional to current lightness
    : l + (1 - l) * factor;     // lighten: proportional to remaining headroom
  return toHex(hslToRgb(h, s, Math.min(0.96, Math.max(0.04, next))));
}

/** Mixes a colour toward white. `amount` is how much of the brand survives. */
function tintWithWhite(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return toHex({
    r: r * amount + 255 * (1 - amount),
    g: g * amount + 255 * (1 - amount),
    b: b * amount + 255 * (1 - amount),
  });
}

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

function contrastRatio(a: number, b: number): number {
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

/** Ink used on top of the brand when white would be unreadable. */
const DARK_INK = '#101828';

/**
 * Picks whichever of white or dark ink reads better on the brand colour.
 *
 * This matters because the picker offers pale swatches (#A6E0AB, #D6BBAB) alongside dark ones;
 * hardcoding white would make primary buttons illegible for those organizations.
 */
function pickContrast(hex: string): string {
  const brandLum = relativeLuminance(hexToRgb(hex));
  const onWhite = contrastRatio(brandLum, relativeLuminance(hexToRgb('#ffffff')));
  const onDark = contrastRatio(brandLum, relativeLuminance(hexToRgb(DARK_INK)));
  return onWhite >= onDark ? '#ffffff' : DARK_INK;
}

export function deriveBrandTokens(inputHex: string): BrandTokens {
  const brand = isValidHex(inputHex) ? inputHex.trim().toLowerCase() : PLATFORM_BRAND;
  const { r, g, b } = hexToRgb(brand);

  return {
    brand,
    brandHover: shiftLightness(brand, -0.15),
    brandActive: shiftLightness(brand, -0.27),
    brandLight: shiftLightness(brand, 0.26),
    brandContrast: pickContrast(brand),
    tint50: tintWithWhite(brand, 0.04),
    tint100: tintWithWhite(brand, 0.08),
    tint200: tintWithWhite(brand, 0.12),
    ring: `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.24)`,
  };
}

/** Maps the token object onto the CSS custom property names the stylesheets consume. */
export function tokensToCssVars(tokens: BrandTokens): Record<string, string> {
  return {
    '--brand': tokens.brand,
    '--brand-hover': tokens.brandHover,
    '--brand-active': tokens.brandActive,
    '--brand-light': tokens.brandLight,
    '--brand-contrast': tokens.brandContrast,
    '--brand-tint-50': tokens.tint50,
    '--brand-tint-100': tokens.tint100,
    '--brand-tint-200': tokens.tint200,
    '--brand-ring': tokens.ring,
  };
}
